import React, { useEffect, useState } from "react";
import api from "../utils/axios";
import {
  Gift,
  Plus,
  Edit2,
  ToggleLeft,
  ToggleRight,
  CheckCircle,
  XCircle,
  Users,
  Search,
  Calendar,
  DollarSign,
  AlertCircle,
  Award,
} from "lucide-react";

interface PromotionalBonus {
  id: number;
  name: string;
  code: string | null;
  bonus_type: string;
  gc_amount: string | number;
  sc_amount: string | number;
  min_deposit: string | number;
  eligibility_criteria: string;
  max_claims_per_user: number;
  total_claims_allowed: number | null;
  claims_count: number;
  is_active: boolean;
  starts_at: string;
  expires_at: string | null;
  created_at: string;
}

interface BonusClaim {
  id: number;
  bonus_id: number;
  user_id: number;
  gc_awarded: string | number;
  sc_awarded: string | number;
  status: string;
  claimed_at: string;
  user_name?: string;
  user_email?: string;
}

const BonusManagement: React.FC = () => {
  const [bonuses, setBonuses] = useState<PromotionalBonus[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [message, setMessage] = useState<{ text: string; type: "success" | "error" } | null>(null);

  // Modal: Create / Edit Bonus
  const [bonusModal, setBonusModal] = useState<{
    isOpen: boolean;
    mode: "create" | "edit";
    bonusId: number | null;
    name: string;
    code: string;
    bonus_type: string;
    gc_amount: string;
    sc_amount: string;
    min_deposit: string;
    eligibility_criteria: string;
    max_claims_per_user: string;
    expires_at: string;
    is_active: boolean;
  }>({
    isOpen: false,
    mode: "create",
    bonusId: null,
    name: "",
    code: "",
    bonus_type: "custom",
    gc_amount: "10000",
    sc_amount: "2.00",
    min_deposit: "0",
    eligibility_criteria: "all",
    max_claims_per_user: "1",
    expires_at: "",
    is_active: true,
  });

  // Modal: View Claims
  const [claimsModal, setClaimsModal] = useState<{
    isOpen: boolean;
    bonus: PromotionalBonus | null;
    claims: BonusClaim[];
    loading: boolean;
  }>({
    isOpen: false,
    bonus: null,
    claims: [],
    loading: false,
  });

  // Modal: Manual Assignment to User
  const [assignModal, setAssignModal] = useState<{
    isOpen: boolean;
    bonus: PromotionalBonus | null;
    userId: string;
    submitting: boolean;
  }>({
    isOpen: false,
    bonus: null,
    userId: "",
    submitting: false,
  });

  const fetchBonuses = async () => {
    setLoading(true);
    try {
      const res = await api.get("/promotional-bonuses", {
        params: { page, limit: 10, search, status: statusFilter },
      });
      if (res.data?.status) {
        setBonuses(res.data.data || []);
        setTotalPages(res.data.pagination?.totalPages || 1);
        setTotalRecords(res.data.pagination?.totalRecords || 0);
      }
    } catch (err) {
      console.error("Error fetching bonuses:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBonuses();
  }, [page, statusFilter]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchBonuses();
  };

  const handleToggleActive = async (bonus: PromotionalBonus) => {
    try {
      const res = await api.patch(`/promotional-bonuses/${bonus.id}/toggle`);
      if (res.data?.status) {
        setMessage({ text: res.data.message, type: "success" });
        setTimeout(() => setMessage(null), 4000);
        fetchBonuses();
      }
    } catch (err: any) {
      setMessage({ text: err.response?.data?.message || "Failed to toggle status", type: "error" });
      setTimeout(() => setMessage(null), 4000);
    }
  };

  const openCreateModal = () => {
    setBonusModal({
      isOpen: true,
      mode: "create",
      bonusId: null,
      name: "",
      code: "",
      bonus_type: "custom",
      gc_amount: "10000",
      sc_amount: "2.00",
      min_deposit: "0",
      eligibility_criteria: "all",
      max_claims_per_user: "1",
      expires_at: "",
      is_active: true,
    });
  };

  const openEditModal = (b: PromotionalBonus) => {
    setBonusModal({
      isOpen: true,
      mode: "edit",
      bonusId: b.id,
      name: b.name,
      code: b.code || "",
      bonus_type: b.bonus_type,
      gc_amount: String(b.gc_amount),
      sc_amount: String(b.sc_amount),
      min_deposit: String(b.min_deposit || 0),
      eligibility_criteria: b.eligibility_criteria,
      max_claims_per_user: String(b.max_claims_per_user),
      expires_at: b.expires_at ? b.expires_at.split("T")[0] : "",
      is_active: b.is_active,
    });
  };

  const handleSaveBonus = async (e: React.FormEvent) => {
    e.preventDefault();
    const { mode, bonusId, name, code, bonus_type, gc_amount, sc_amount, min_deposit, eligibility_criteria, max_claims_per_user, expires_at, is_active } = bonusModal;

    try {
      const payload = {
        name,
        code: code.trim() || null,
        bonus_type,
        gc_amount: parseFloat(gc_amount) || 0,
        sc_amount: parseFloat(sc_amount) || 0,
        min_deposit: parseFloat(min_deposit) || 0,
        eligibility_criteria,
        max_claims_per_user: parseInt(max_claims_per_user, 10) || 1,
        expires_at: expires_at ? new Date(expires_at).toISOString() : null,
        is_active,
      };

      if (mode === "create") {
        await api.post("/promotional-bonuses", payload);
        setMessage({ text: "Bonus created successfully!", type: "success" });
      } else {
        await api.put(`/promotional-bonuses/${bonusId}`, payload);
        setMessage({ text: "Bonus updated successfully!", type: "success" });
      }

      setBonusModal((prev) => ({ ...prev, isOpen: false }));
      setTimeout(() => setMessage(null), 4000);
      fetchBonuses();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to save bonus");
    }
  };

  const openClaimsModal = async (bonus: PromotionalBonus) => {
    setClaimsModal({ isOpen: true, bonus, claims: [], loading: true });
    try {
      const res = await api.get(`/promotional-bonuses/${bonus.id}/claims`);
      if (res.data?.status) {
        setClaimsModal((prev) => ({ ...prev, claims: res.data.data || [], loading: false }));
      }
    } catch (err) {
      console.error("Error loading claims:", err);
      setClaimsModal((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleAssignBonus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!assignModal.bonus || !assignModal.userId) return;

    setAssignModal((prev) => ({ ...prev, submitting: true }));
    try {
      const res = await api.post("/promotional-bonuses/assign-user", {
        bonusId: assignModal.bonus.id,
        userId: assignModal.userId.trim(),
      });
      setMessage({ text: res.data.message || "Bonus awarded successfully!", type: "success" });
      setTimeout(() => setMessage(null), 5000);
      setAssignModal({ isOpen: false, bonus: null, userId: "", submitting: false });
      fetchBonuses();
    } catch (err: any) {
      alert(err.response?.data?.message || "Failed to award bonus to user");
      setAssignModal((prev) => ({ ...prev, submitting: false }));
    }
  };

  return (
    <div className="min-h-screen">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Bonus & Promotion Management
          </h1>
          <p className="text-sm text-gray-500">
            Configure promotional bonuses, track player claims, and award dual-currency rewards.
          </p>
        </div>

        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl text-sm font-bold shadow transition active:scale-95"
        >
          <Plus size={16} />
          Create New Bonus
        </button>
      </div>

      {message && (
        <div
          className={`mb-4 p-3 rounded-lg text-sm font-semibold border ${
            message.type === "success"
              ? "bg-green-50 text-green-800 border-green-200"
              : "bg-red-50 text-red-800 border-red-200"
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Filter and Search Bar */}
      <div className="flex flex-col md:flex-row gap-3 mb-6 items-center justify-between">
        <form onSubmit={handleSearchSubmit} className="flex gap-2 w-full md:w-96">
          <input
            type="text"
            placeholder="Search bonus name, code, type..."
            className="border rounded-lg px-4 py-2 text-sm w-full focus:ring-2 focus:ring-indigo-500"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="submit"
            className="bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            Search
          </button>
        </form>

        <div className="inline-flex rounded-lg border bg-white p-1 shadow-sm text-xs font-semibold">
          <button
            onClick={() => { setStatusFilter("all"); setPage(1); }}
            className={`px-3 py-1.5 rounded-md transition ${statusFilter === "all" ? "bg-gray-800 text-white" : "text-gray-600 hover:text-gray-900"}`}
          >
            All ({totalRecords})
          </button>
          <button
            onClick={() => { setStatusFilter("active"); setPage(1); }}
            className={`px-3 py-1.5 rounded-md transition ${statusFilter === "active" ? "bg-emerald-600 text-white" : "text-gray-600 hover:text-gray-900"}`}
          >
            Active Only
          </button>
          <button
            onClick={() => { setStatusFilter("inactive"); setPage(1); }}
            className={`px-3 py-1.5 rounded-md transition ${statusFilter === "inactive" ? "bg-gray-400 text-white" : "text-gray-600 hover:text-gray-900"}`}
          >
            Inactive
          </button>
        </div>
      </div>

      {/* Bonuses Table */}
      <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-700 text-xs uppercase tracking-wider">
            <tr>
              <th className="px-4 py-3 text-left font-bold">#</th>
              <th className="px-4 py-3 text-left font-bold">Bonus Name & Code</th>
              <th className="px-4 py-3 text-left font-bold">Type</th>
              <th className="px-4 py-3 text-left font-bold">Reward Amounts</th>
              <th className="px-4 py-3 text-left font-bold">Eligibility</th>
              <th className="px-4 py-3 text-left font-bold">Claims</th>
              <th className="px-4 py-3 text-left font-bold">Status</th>
              <th className="px-4 py-3 text-center font-bold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={8} className="py-10 text-center text-gray-400">Loading bonuses...</td>
              </tr>
            ) : bonuses.length === 0 ? (
              <tr>
                <td colSpan={8} className="py-10 text-center text-gray-400">No promotional bonuses found.</td>
              </tr>
            ) : (
              bonuses.map((b, idx) => (
                <tr key={b.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs text-gray-500">
                    {(page - 1) * 10 + idx + 1}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-bold text-gray-900">{b.name}</div>
                    {b.code && (
                      <span className="inline-block mt-0.5 px-2 py-0.5 rounded text-[10px] font-mono font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                        {b.code}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 capitalize text-xs text-gray-700">
                    {b.bonus_type}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-bold text-amber-600 text-xs">
                      🪙 {Number(b.gc_amount).toLocaleString()} GC
                    </div>
                    <div className="font-bold text-sky-600 text-xs">
                      ✨ {Number(b.sc_amount).toFixed(2)} SC
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600 capitalize">
                    {b.eligibility_criteria.replace("_", " ")}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => openClaimsModal(b)}
                      className="text-xs font-bold text-indigo-600 hover:text-indigo-800 underline flex items-center gap-1 border-none bg-transparent cursor-pointer"
                    >
                      <Users size={12} />
                      {b.claims_count} claimed
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleActive(b)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold transition border-none cursor-pointer ${
                        b.is_active
                          ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      {b.is_active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                      {b.is_active ? "Active" : "Inactive"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-center whitespace-nowrap space-x-2">
                    <button
                      onClick={() => openEditModal(b)}
                      className="px-2.5 py-1 rounded border text-xs font-semibold text-gray-700 hover:bg-gray-100 transition"
                      title="Edit bonus"
                    >
                      <Edit2 size={12} className="inline mr-1" />
                      Edit
                    </button>
                    <button
                      onClick={() => setAssignModal({ isOpen: true, bonus: b, userId: "", submitting: false })}
                      className="px-2.5 py-1 rounded bg-indigo-50 border border-indigo-200 text-xs font-bold text-indigo-700 hover:bg-indigo-100 transition"
                      title="Award to user"
                    >
                      <Award size={12} className="inline mr-1" />
                      Award
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Create / Edit Bonus Modal */}
      {bonusModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl border max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-bold text-gray-900 mb-1">
              {bonusModal.mode === "create" ? "Create Promotional Bonus" : "Edit Promotional Bonus"}
            </h3>
            <p className="text-xs text-gray-500 mb-4">
              Configure reward parameters, GC/SC amounts, and player eligibility.
            </p>

            <form onSubmit={handleSaveBonus} className="space-y-4 text-xs">
              <div>
                <label className="block font-bold text-gray-700 uppercase mb-1">Bonus Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. VIP High Roller Welcome"
                  className="w-full border rounded-lg p-2.5 text-sm"
                  value={bonusModal.name}
                  onChange={(e) => setBonusModal({ ...bonusModal, name: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-gray-700 uppercase mb-1">Promo Code (Optional)</label>
                  <input
                    type="text"
                    placeholder="e.g. VIP2026"
                    className="w-full border rounded-lg p-2 text-sm font-mono uppercase"
                    value={bonusModal.code}
                    onChange={(e) => setBonusModal({ ...bonusModal, code: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block font-bold text-gray-700 uppercase mb-1">Bonus Type</label>
                  <select
                    className="w-full border rounded-lg p-2 text-sm bg-white"
                    value={bonusModal.bonus_type}
                    onChange={(e) => setBonusModal({ ...bonusModal, bonus_type: e.target.value })}
                  >
                    <option value="welcome">Welcome</option>
                    <option value="daily">Daily Login</option>
                    <option value="reload">Reload</option>
                    <option value="vip">VIP Loyalty</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 p-3 rounded-xl bg-amber-50/50 border border-amber-200">
                <div>
                  <label className="block font-bold text-amber-800 uppercase mb-1">🪙 Gold Coins (GC)</label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    className="w-full border rounded-lg p-2 text-sm font-bold"
                    value={bonusModal.gc_amount}
                    onChange={(e) => setBonusModal({ ...bonusModal, gc_amount: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block font-bold text-sky-800 uppercase mb-1">✨ Sweep Coins (SC)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="w-full border rounded-lg p-2 text-sm font-bold"
                    value={bonusModal.sc_amount}
                    onChange={(e) => setBonusModal({ ...bonusModal, sc_amount: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block font-bold text-gray-700 uppercase mb-1">Eligibility Criteria</label>
                  <select
                    className="w-full border rounded-lg p-2 text-sm bg-white"
                    value={bonusModal.eligibility_criteria}
                    onChange={(e) => setBonusModal({ ...bonusModal, eligibility_criteria: e.target.value })}
                  >
                    <option value="all">All Players</option>
                    <option value="new_users">New Registrations</option>
                    <option value="kyc_verified">KYC Verified Only</option>
                    <option value="vip">VIP Tier</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-gray-700 uppercase mb-1">Expiration Date</label>
                  <input
                    type="date"
                    className="w-full border rounded-lg p-2 text-sm"
                    value={bonusModal.expires_at}
                    onChange={(e) => setBonusModal({ ...bonusModal, expires_at: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <input
                  type="checkbox"
                  id="bonus_active"
                  checked={bonusModal.is_active}
                  onChange={(e) => setBonusModal({ ...bonusModal, is_active: e.target.checked })}
                  className="rounded text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                />
                <label htmlFor="bonus_active" className="text-xs font-semibold text-gray-700 cursor-pointer">
                  Activate this bonus immediately
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setBonusModal((prev) => ({ ...prev, isOpen: false }))}
                  className="px-4 py-2 border rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold shadow"
                >
                  {bonusModal.mode === "create" ? "Create Bonus" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manual Award to User Modal */}
      {assignModal.isOpen && assignModal.bonus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border">
            <h3 className="text-lg font-bold text-gray-900 mb-1">Award Bonus to Player</h3>
            <p className="text-xs text-gray-500 mb-4">
              Credit <strong>{assignModal.bonus.name}</strong> to a specific player with duplicate checks.
            </p>

            <div className="p-3 bg-indigo-50 rounded-xl mb-4 text-xs text-indigo-900 space-y-1">
              <div><strong>Reward:</strong> 🪙 {Number(assignModal.bonus.gc_amount).toLocaleString()} GC &nbsp;|&nbsp; ✨ {Number(assignModal.bonus.sc_amount).toFixed(2)} SC</div>
              <div><strong>Max Claims:</strong> {assignModal.bonus.max_claims_per_user} per player</div>
            </div>

            <form onSubmit={handleAssignBonus} className="space-y-4">
              <div>
                <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Target User ID (UID) *</label>
                <input
                  type="text"
                  required
                  placeholder="Enter Player UID (e.g. 5365129438)"
                  className="w-full border rounded-lg p-2.5 text-sm font-mono"
                  value={assignModal.userId}
                  onChange={(e) => setAssignModal({ ...assignModal, userId: e.target.value })}
                />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setAssignModal({ isOpen: false, bonus: null, userId: "", submitting: false })}
                  className="px-4 py-2 border rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={assignModal.submitting}
                  className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold shadow disabled:opacity-50"
                >
                  {assignModal.submitting ? "Awarding..." : "Confirm & Award Bonus"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bonus Claims List Modal */}
      {claimsModal.isOpen && claimsModal.bonus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm animate-fade-in">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl border max-h-[85vh] flex flex-col">
            <div className="flex justify-between items-center pb-4 border-b">
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Claims History: {claimsModal.bonus.name}
                </h3>
                <p className="text-xs text-gray-500">
                  Verified claims and balance settlement history
                </p>
              </div>
              <button
                onClick={() => setClaimsModal({ isOpen: false, bonus: null, claims: [], loading: false })}
                className="text-gray-400 hover:text-gray-600 border-none bg-transparent text-lg font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-y-auto py-4">
              {claimsModal.loading ? (
                <div className="text-center py-8 text-gray-400 text-xs">Loading claims history...</div>
              ) : claimsModal.claims.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-xs">No claims recorded yet for this bonus.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-3 py-2 text-left font-bold">Player</th>
                      <th className="px-3 py-2 text-left font-bold">UID</th>
                      <th className="px-3 py-2 text-left font-bold">GC Credited</th>
                      <th className="px-3 py-2 text-left font-bold">SC Credited</th>
                      <th className="px-3 py-2 text-left font-bold">Claimed At</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {claimsModal.claims.map((c) => (
                      <tr key={c.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-semibold text-gray-800">{c.user_name || "User"}</td>
                        <td className="px-3 py-2 font-mono">{c.user_id}</td>
                        <td className="px-3 py-2 text-amber-600 font-bold">🪙 {Number(c.gc_awarded).toLocaleString()}</td>
                        <td className="px-3 py-2 text-sky-600 font-bold">✨ {Number(c.sc_awarded).toFixed(2)}</td>
                        <td className="px-3 py-2 text-gray-500">{new Date(c.claimed_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="pt-3 border-t flex justify-end">
              <button
                onClick={() => setClaimsModal({ isOpen: false, bonus: null, claims: [], loading: false })}
                className="px-4 py-2 border rounded-lg text-xs font-semibold text-gray-700 hover:bg-gray-100"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BonusManagement;

